import { useEffect, useRef, useState } from 'react';

import { EraserIcon } from '@radix-ui/react-icons';
import { PipetteIcon } from '../../svg/pipette-icon';
import { Button } from '../button';
import { Input } from '../input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

type HsvColor = { h: number; s: number; v: number };
type EyeDropperInstance = { open: () => Promise<{ sRGBHex: string }> };
type EyeDropperWindow = Window & { EyeDropper?: new () => EyeDropperInstance };

const DEFAULT_COLOR = '#000000';

function normalizeHex(color: string | null | undefined): string | undefined {
  const normalized = color?.match(/^#?([\da-f]{6})$/i)?.[1];
  return normalized ? `#${normalized.toUpperCase()}` : undefined;
}

function hexToHsv(color: string | undefined): HsvColor {
  const hex = color?.match(/^#?([\da-f]{6})$/i)?.[1] ?? DEFAULT_COLOR.slice(1);
  const [red, green, blue] = [0, 2, 4].map(
    (offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255,
  );
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const delta = maximum - minimum;

  if (!delta) return { h: 0, s: 0, v: Math.round(maximum * 100) };

  const saturation = delta / maximum;
  let hue = 0;
  if (maximum === red) hue = ((green - blue) / delta) % 6;
  else if (maximum === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;

  return {
    h: Math.round((hue * 60 + 360) % 360),
    s: Math.round(saturation * 100),
    v: Math.round(maximum * 100),
  };
}

function hsvToHex({ h, s, v }: HsvColor): string {
  const saturation = s / 100;
  const value = v / 100;
  const chroma = value * saturation;
  const hueSegment = h / 60;
  const secondary = chroma * (1 - Math.abs((hueSegment % 2) - 1));
  const [red, green, blue] =
    hueSegment < 1
      ? [chroma, secondary, 0]
      : hueSegment < 2
        ? [secondary, chroma, 0]
        : hueSegment < 3
          ? [0, chroma, secondary]
          : hueSegment < 4
            ? [0, secondary, chroma]
            : hueSegment < 5
              ? [secondary, 0, chroma]
              : [chroma, 0, secondary];
  const match = value - chroma;

  return `#${[red, green, blue]
    .map((channel) =>
      Math.round((channel + match) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`.toUpperCase();
}

type ColorPickerProps = {
  colors?: string[];
  isCleanColorVisible?: boolean;
  noneColorLabel?: string;
  value?: string | null;
  onValueChange?: (color?: string) => void;
};

export function ColorPicker({
  colors = [],
  isCleanColorVisible,
  noneColorLabel = 'Sin color',
  value,
  onValueChange,
}: ColorPickerProps) {
  const normalizedValue = normalizeHex(value);
  const [selectedColor, setSelectedColor] = useState<string | undefined>(normalizedValue);
  const [hsv, setHsv] = useState(() => hexToHsv(normalizedValue));
  const [hexInput, setHexInput] = useState(selectedColor ?? '');
  const [isEyeDropperAvailable, setIsEyeDropperAvailable] = useState(false);
  const lastEmittedColorRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (normalizedValue === lastEmittedColorRef.current) return;
    setSelectedColor(normalizedValue);
    if (normalizedValue) {
      setHsv(hexToHsv(normalizedValue));
    }
  }, [normalizedValue]);

  useEffect(() => setHexInput(selectedColor ?? ''), [selectedColor]);
  useEffect(() => {
    setIsEyeDropperAvailable(
      typeof window !== 'undefined' && Boolean((window as EyeDropperWindow).EyeDropper),
    );
  }, []);

  const setColor = (nextColor: HsvColor) => {
    const nextHex = hsvToHex(nextColor);
    lastEmittedColorRef.current = nextHex;
    setHsv(nextColor);
    setSelectedColor(nextHex);
    onValueChange?.(nextHex);
  };

  const clearColor = () => {
    lastEmittedColorRef.current = undefined;
    setSelectedColor(undefined);
    onValueChange?.(undefined);
  };

  const updateSaturationAndValue = (event: React.PointerEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const saturation = Math.round(
      Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)) * 100,
    );
    const value = Math.round(
      (1 - Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height))) * 100,
    );
    setColor({ ...hsv, s: saturation, v: value });
  };

  const pickColorFromScreen = async () => {
    const EyeDropper = (window as EyeDropperWindow).EyeDropper;
    if (!EyeDropper) return;

    try {
      const { sRGBHex } = await new EyeDropper().open();
      setColor(hexToHsv(sRGBHex));
    } catch {
      // The user can dismiss the browser's native picker with Escape.
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="h-8 w-full justify-start rounded-lg px-2" size="sm" variant="outline">
          <span
            className="size-4 rounded-full border"
            style={{ backgroundColor: selectedColor ?? 'transparent' }}
          />
          {selectedColor ? (
            <span className="font-mono text-sm uppercase">{selectedColor}</span>
          ) : (
            <span className="text-sm text-muted-foreground">{noneColorLabel}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3 p-3">
        <button
          aria-label="Saturación y brillo"
          className="relative h-40 w-full cursor-crosshair rounded-md"
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h} 100% 50%))`,
          }}
          type="button"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            updateSaturationAndValue(event);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId))
              updateSaturationAndValue(event);
          }}
        >
          <span
            className="pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm"
            style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%` }}
          />
        </button>
        <input
          aria-label="Tono"
          className="h-2 w-full cursor-ew-resize appearance-none rounded-full accent-accent/80"
          max="360"
          min="0"
          style={{
            background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
          }}
          type="range"
          value={hsv.h}
          onChange={(event) => setColor({ ...hsv, h: Number(event.target.value) })}
        />
        {colors.length > 0 && (
          <div className="grid grid-cols-6 gap-2">
            {colors.slice(0, 10).map((color) => {
              const presetColor = hsvToHex(hexToHsv(color));

              return (
                <button
                  aria-label={`Seleccionar ${presetColor}`}
                  title={`${presetColor}`}
                  className={`size-8 rounded-full border transition-transform hover:scale-110 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden ${selectedColor === presetColor ? 'ring-1 ring-ring' : ''}`}
                  key={color}
                  style={{ backgroundColor: presetColor }}
                  type="button"
                  onClick={() => setColor(hexToHsv(presetColor))}
                />
              );
            })}
            {isCleanColorVisible && (
              <button
                aria-label={noneColorLabel}
                title={noneColorLabel}
                className={`flex size-8 items-center justify-center rounded-full border transition-transform hover:scale-110 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden ${selectedColor ? '' : 'ring-1 ring-ring'}`}
                style={{ backgroundColor: 'transparent' }}
                type="button"
                onClick={clearColor}
              >
                <EraserIcon className="size-4 text-destructive/50" />
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {isEyeDropperAvailable && (
            <Button
              title="Selector de color"
              className="shrink-0"
              size="icon-sm"
              variant="outline"
              onClick={pickColorFromScreen}
            >
              <PipetteIcon />
            </Button>
          )}
          <span
            className="size-8 shrink-0 rounded-full border"
            style={{ backgroundColor: selectedColor ?? 'transparent' }}
          />
          <Input
            aria-label="Color hexadecimal"
            className="font-mono uppercase"
            sizesx="sm"
            value={hexInput}
            onBlur={() => setHexInput(selectedColor ?? '')}
            onChange={(event) => {
              const nextHex = event.target.value.toUpperCase();
              setHexInput(nextHex);
              if (/^#[\dA-F]{6}$/.test(nextHex)) setColor(hexToHsv(nextHex));
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
